import { Inventory, InventoryItem } from '../utils/Inventory';
import { VendingMachineUI } from './VendingMachineUI';

export class InventoryUI {
  private static instance: InventoryUI;
  private inventory: Inventory;
  private container!: HTMLDivElement;
  private isOpen: boolean = false;
  private button!: HTMLButtonElement;
  private notificationBadge: HTMLDivElement | null = null;

  private constructor() {
    this.inventory = Inventory.getInstance();
    this.createUI();
  }

  public static getInstance(): InventoryUI {
    if (!InventoryUI.instance) {
      InventoryUI.instance = new InventoryUI();
    }
    return InventoryUI.instance;
  }

  private createUI(): void {
    // Create container
    this.container = document.createElement('div');
    this.container.className = 'inventory-overlay';
    this.container.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      max-width: 800px;
      height: 80%;
      max-height: 600px;
      background: rgba(0, 0, 0, 0.9);
      border: 2px solid #9932cc;
      border-radius: 10px;
      padding: 20px;
      display: none;
      z-index: 1000;
      color: white;
      font-family: Arial, sans-serif;
      overflow-y: auto;
    `;

    // Create header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 2px solid #9932cc;
    `;

    const title = document.createElement('h2');
    title.textContent = 'Inventory';
    title.style.cssText = `
      margin: 0;
      color: #9932cc;
      font-size: 24px;
    `;

    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.style.cssText = `
      background: none;
      border: none;
      color: #9932cc;
      font-size: 24px;
      cursor: pointer;
      padding: 5px 10px;
    `;
    closeButton.onclick = () => this.hide();

    header.appendChild(title);
    header.appendChild(closeButton);
    this.container.appendChild(header);

    // Create grid container for items
    const gridContainer = document.createElement('div');
    gridContainer.className = 'inventory-grid';
    gridContainer.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 20px;
      padding: 10px;
    `;
    this.container.appendChild(gridContainer);

    // Create inventory button
    this.button = document.createElement('button');
    this.button.className = 'inventory-button';
    this.button.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 50px;
      height: 50px;
      background: #9932cc;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 5px rgba(0,0,0,0.2);
      transition: transform 0.2s;
    `;

    // Add backpack icon
    this.button.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
        <path d="M20 8h-3V4H7v4H4c-1.1 0-2 .9-2 2v10h20V10c0-1.1-.9-2-2-2zm-1 10H5v-8h14v8zM9 6h6v2H9V6z"/>
      </svg>
    `;

    this.button.onmouseover = () => {
      this.button.style.transform = 'scale(1.1)';
    };
    this.button.onmouseout = () => {
      this.button.style.transform = 'scale(1)';
    };
    this.button.onclick = () => this.toggle();

    // Add elements to document
    document.body.appendChild(this.container);
    document.body.appendChild(this.button);
  }

  private createItemElement(item: InventoryItem): HTMLDivElement {
    const itemElement = document.createElement('div');
    itemElement.className = 'inventory-item';
    itemElement.style.cssText = `
      background: rgba(153, 50, 204, 0.2);
      border: 1px solid #9932cc;
      border-radius: 5px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      transition: transform 0.2s;
      cursor: pointer;
      position: relative;
    `;

    const rarityColors = {
      common: '#cccccc',
      rare: '#4169e1',
      epic: '#9932cc',
      legendary: '#ffd700'
    };

    // Try to get the 3D rendered preview from VendingMachineUI
    const renderedPreview = VendingMachineUI.renderedPreviews.get(item.name);
    
    // Choose the image source - prefer the 3D rendered preview if available
    const imageSource = renderedPreview || item.image;
    
    // Debug the image source being used
    console.log('[InventoryUI] Creating item:', item.name, ', using rendered preview:', !!renderedPreview);
    
    itemElement.innerHTML = `
      <div class="item-image-container" style="
        width: 100px;
        height: 100px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 2px solid ${rarityColors[item.rarity]};
        border-radius: 5px;
        overflow: hidden;
        background-color: ${renderedPreview ? '#333333' : 'transparent'};
      ">
        <img src="${imageSource}" alt="${item.name}" style="
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        " onerror="console.error('Failed to load image:', this.src); this.src='/assets/default-toy.png';">
      </div>
      <div class="item-name" style="
        color: ${rarityColors[item.rarity]};
        font-weight: bold;
        text-align: center;
      ">${item.name}</div>
      <div class="item-rarity" style="
        color: ${rarityColors[item.rarity]};
        font-size: 12px;
        text-transform: uppercase;
      ">${item.rarity}</div>
    `;

    itemElement.onmouseover = () => {
      itemElement.style.transform = 'scale(1.05)';
    };
    itemElement.onmouseout = () => {
      itemElement.style.transform = 'scale(1)';
    };

    // Show description on click
    itemElement.onclick = () => {
      const tooltip = document.createElement('div');
      tooltip.style.cssText = `
        position: absolute;
        bottom: 120%;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.9);
        border: 1px solid ${rarityColors[item.rarity]};
        border-radius: 5px;
        padding: 10px;
        width: 200px;
        color: white;
        font-size: 14px;
        text-align: center;
        z-index: 1001;
      `;
      tooltip.textContent = item.description;

      const existingTooltip = itemElement.querySelector('.tooltip');
      if (existingTooltip) {
        itemElement.removeChild(existingTooltip);
      } else {
        itemElement.appendChild(tooltip);
        setTimeout(() => {
          if (tooltip.parentElement === itemElement) {
            itemElement.removeChild(tooltip);
          }
        }, 3000);
      }
    };

    return itemElement;
  }

  public updateItems(): void {
    const gridContainer = this.container.querySelector('.inventory-grid');
    if (!gridContainer) return;

    gridContainer.innerHTML = '';
    const items = this.inventory.getItems();

    if (items.length === 0) {
      const emptyMessage = document.createElement('div');
      emptyMessage.style.cssText = `
        grid-column: 1 / -1;
        text-align: center;
        color: #666;
        padding: 20px;
      `;
      emptyMessage.textContent = 'Your inventory is empty. Visit the vending machine to get some toys!';
      gridContainer.appendChild(emptyMessage);
      return;
    }

    items.forEach(item => {
      gridContainer.appendChild(this.createItemElement(item));
    });
  }

  public show(): void {
    this.container.style.display = 'block';
    this.updateItems();
    this.isOpen = true;
    
    // Remove notification badge when inventory is opened
    this.hideNotification();
  }

  public hide(): void {
    this.container.style.display = 'none';
    this.isOpen = false;
  }

  public toggle(): void {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  public showNotification(): void {
    // If inventory is open, don't show notification
    if (this.isOpen) return;
    
    // Remove existing notification if present
    this.hideNotification();
    
    // Create notification badge
    this.notificationBadge = document.createElement('div');
    this.notificationBadge.style.cssText = `
      position: absolute;
      top: -5px;
      right: -5px;
      width: 15px;
      height: 15px;
      background-color: #ff3333;
      border-radius: 50%;
      border: 2px solid #9932cc;
      animation: pulse 1.5s infinite;
    `;
    
    // Add animation style
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    
    // Add badge to button
    this.button.appendChild(this.notificationBadge);
  }

  public hideNotification(): void {
    if (this.notificationBadge && this.notificationBadge.parentNode) {
      this.notificationBadge.parentNode.removeChild(this.notificationBadge);
      this.notificationBadge = null;
    }
  }
} 